import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const CommunityImpactSection = ({ onWhatsAppClick }) => {
  const [isVisible, setIsVisible] = useState(true); // Changed to true for mobile compatibility
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 } // Lowered threshold for better mobile detection
    );

    if (sectionRef?.current) {
      observer?.observe(sectionRef?.current);
    }

    return () => observer?.disconnect();
  }, []);

  const initiatives = [
    {
      id: 1,
      title: "Responsible Gaming Education",
      description: "Free workshops and resources to promote safe gaming practices across India. We've educated over 25,000 players about responsible gaming.",
      impact: "25,000+ Players Educated",
      image: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=400&h=300&fit=crop&crop=center",
      icon: "GraduationCap",
      category: "Education"
    },
    {
      id: 2,
      title: "Digital Literacy Program",
      description: "Teaching rural communities about digital payments and online safety. Partnering with NGOs to bridge the digital divide.",
      impact: "5,000+ Rural Families Helped",
      image: "https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?w=400&h=300&fit=crop&crop=center",
      icon: "Smartphone",
      category: "Technology"
    },
    {
      id: 3,
      title: "Youth Sports Sponsorship",
      description: "Supporting local cricket and kabaddi teams across India. Providing equipment, training, and tournament opportunities for young athletes.",
      impact: "50+ Teams Sponsored",
      image: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=300&fit=crop&crop=center",
      icon: "Trophy",
      category: "Sports"
    },
    {
      id: 4,
      title: "Financial Literacy Workshops",
      description: "Teaching communities about smart money management, savings, and investment strategies. Empowering people with financial knowledge.",
      impact: "10,000+ People Trained",
      image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=300&fit=crop&crop=center",
      icon: "PiggyBank",
      category: "Finance"
    }
  ];

  const partnerships = [
    {
      name: "Akshaya Patra Foundation",
      description: "Supporting mid-day meal programs for underprivileged children",
      contribution: "₹50 Lakhs Donated",
      logo: "https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?w=100&h=100&fit=crop&crop=center"
    },
    {
      name: "Teach for India",
      description: "Funding educational programs in rural and urban communities",
      contribution: "₹25 Lakhs Donated",
      logo: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=100&h=100&fit=crop&crop=center"
    },
    {
      name: "Digital India Foundation",
      description: "Promoting digital literacy and technology adoption",
      contribution: "₹30 Lakhs Donated",
      logo: "https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?w=100&h=100&fit=crop&crop=center"
    }
  ];

  const responsibleGaming = [
    {
      icon: "Clock",
      title: "Time Limits",
      description: "Set daily, weekly, or monthly time limits for gaming sessions"
    },
    {
      icon: "DollarSign",
      title: "Spending Controls",
      description: "Deposit limits and loss limits to manage your gaming budget"
    },
    {
      icon: "Pause",
      title: "Self-Exclusion",
      description: "Take breaks from gaming with our self-exclusion tools"
    },
    {
      icon: "Phone",
      title: "Support Helpline",
      description: "24/7 counseling support for gaming-related concerns"
    },
    {
      icon: "BookOpen",
      title: "Educational Resources",
      description: "Comprehensive guides on responsible gaming practices"
    },
    {
      icon: "Users",
      title: "Family Support",
      description: "Resources and guidance for family members and friends"
    }
  ];

  return (
    <section ref={sectionRef} className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-royal rounded-full flex items-center justify-center shadow-royal">
              <Icon name="Heart" size={32} color="white" />
            </div>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-gray-900 mb-6">
            Community <span className="text-gradient-royal">Impact</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Beyond gaming, we're committed to making a positive impact on Indian communities through education, technology, and responsible gaming initiatives.
          </p>
        </div>

        {/* Initiatives Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-20">
          {initiatives?.map((initiative, index) => (
            <div
              key={initiative?.id}
              className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${index * 200}ms` }}
            >
              <div className="bg-white rounded-2xl shadow-subtle border border-gray-100 overflow-hidden hover:shadow-royal transition-all duration-300 hover-lift">
                {/* Initiative Image */}
                <div className="relative h-48">
                  <Image
                    src={initiative?.image}
                    alt={initiative?.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1 bg-primary/90 text-white text-xs font-bold rounded-full">
                      {initiative?.category}
                    </span>
                  </div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gradient-royal rounded-full flex items-center justify-center">
                        <Icon name={initiative?.icon} size={24} color="white" />
                      </div>
                      <div>
                        <p className="text-white font-bold text-lg">{initiative?.impact}</p>
                        <p className="text-gray-200 text-sm">Impact Achieved</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Initiative Info */}
                <div className="p-6">
                  <h3 className="text-xl font-heading font-bold text-gray-900 mb-3">{initiative?.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{initiative?.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Partnerships */}
        <div className={`mb-20 transition-all duration-1000 delay-800 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h3 className="text-2xl font-heading font-bold text-center text-gray-900 mb-12">
            Charitable <span className="text-gradient-royal">Partnerships</span>
          </h3>
          
          <div className="grid md:grid-cols-3 gap-8">
            {partnerships?.map((partner, index) => (
              <div
                key={index}
                className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${(index + 4) * 200}ms` }}
              >
                <div className="bg-white rounded-xl p-6 shadow-subtle border border-gray-100 hover:shadow-royal transition-all duration-300 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full overflow-hidden border-2 border-gray-100">
                    <Image
                      src={partner?.logo}
                      alt={partner?.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">{partner?.name}</h4>
                  <p className="text-gray-600 text-sm mb-4">{partner?.description}</p>
                  <div className="bg-success/20 text-success px-3 py-2 rounded-full text-sm font-bold">
                    {partner?.contribution}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Responsible Gaming */}
        <div className={`mb-16 transition-all duration-1000 delay-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h3 className="text-2xl font-heading font-bold text-center text-gray-900 mb-12">
            Responsible <span className="text-gradient-royal">Gaming Tools</span>
          </h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {responsibleGaming?.map((tool, index) => (
              <div
                key={index}
                className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${(index + 7) * 150}ms` }}
              >
                <div className="bg-white rounded-xl p-6 shadow-subtle border border-gray-100 hover:shadow-royal transition-all duration-300">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                    <Icon name={tool?.icon} size={24} color="var(--color-primary)" />
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">{tool?.title}</h4>
                  <p className="text-gray-600 text-sm">{tool?.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className={`text-center transition-all duration-1000 delay-1200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-2xl p-8 border border-primary/20">
            <h3 className="text-2xl font-heading font-bold text-gray-900 mb-4">
              Join Our Responsible Gaming Community
            </h3>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Experience gaming the right way with Nitin. Our commitment to responsible gaming ensures you have fun while staying in control.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="default"
                size="lg"
                onClick={onWhatsAppClick}
                iconName="MessageCircle"
                iconPosition="left"
                className="bg-gradient-royal hover:shadow-glow animate-pulse-golden hover:animate-none"
              >
                Learn About Safe Gaming
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="border-primary text-primary hover:bg-primary hover:text-white"
              >
                View Our Impact Report
              </Button>
            </div>

            {/* Impact Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-8 pt-8 border-t border-gray-200">
              <div>
                <p className="text-3xl font-bold text-gray-900 mb-2">₹1.5Cr+</p>
                <p className="text-gray-600 text-sm">Donated to Charity</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-gray-900 mb-2">40K+</p>
                <p className="text-gray-600 text-sm">People Impacted</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-gray-900 mb-2">15+</p>
                <p className="text-gray-600 text-sm">NGO Partners</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-gray-900 mb-2">100%</p>
                <p className="text-gray-600 text-sm">Responsible Gaming</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CommunityImpactSection;